import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import ICONS from "../../../../assets/Icons";
import { getCreateTransactionBalance } from "../../../../redux/features/owner/ownerAllVerification/ownerAllVerification";
import { GlobalConfiguration } from "../../../../utils/configurationProvider";
import formatTotalBalance from "../../../../utils/formatTotalBalance";
import globalText from "../../../../utils/globalText";
import UniversalRefreshToast from "../../../_utils/UniversalRefreshToast";
import Loader from "../../../Loader/Index";
import useServiceChecker from "../../../ServiceChecker";
import CommonBankLogo from "../../CommonBankLogo";
import IconTearDropButton from "../../CommonButtons/IconTearDropButton";
import PrimaryWithIconButton from "../../CommonButtons/PrimaryWithIconButton";
import CommonToast from "../../CommonToast";
import CardActionableButton from "../../CommonViewAllCard/CardSubComponent/CardActionableButton";
import { formatAccountNumber } from "../../FormatAccountNumber";
import SVGComponent from "../../SVGComponent";
import TickerAnimation from "../../TickerAnimation";

/* =============================================================================
 * CommonDestinationAccount
 *
 * Reusable destination account component.
 *
 * IMPORTANT:
 * This component does NOT own the account-selection popup.
 *
 * Parent component owns:
 * - Account selection popup
 * - Popup open/close state
 * - Account selection API
 * - Selected account handling
 *
 * This component owns:
 * - Destination account UI
 * - Select/Edit/Create actions
 * - Source account dependency
 * - Balance display/refresh (internal flow only -> showsBalance)
 * - Associated account CTA
 * - Enabled/disabled states
 *
 * BALANCE / REFRESH:
 * - Balance tile + refresh button are shown ONLY when `showsBalance` is true
 *   (parent passes true only for the internal flow).
 * - Refresh button is visible only when the destination owner is active AND
 *   the user has "VAccountBalance" or "Admin" service.
 * ========================================================================== */

/* =============================================================================
 * 1. DEFAULT PAYLOAD KEYS
 * ========================================================================== */

const DEFAULT_PAYLOAD_KEYS = Object.freeze({
  accountNumber: "bankAccountNo",
  ifsc: "bankAccountIFSC",
  accountName: "bankAccountName",
  fallbackAccountName: "destinationAccountName",
  ownerId: "destinationAcountOwnerId",
  isOwnerActive: "isDestinationOwnerActive",
  balance: "destinationAccountBalance",
  balanceLastUpdated: "destinationAcountOwnerLastUpdated",
});

/* =============================================================================
 * 2. BALANCE REFRESH HOOK
 * ========================================================================== */

const IDLE_REFRESH_SESSION = {
  isActive: false,
  isCompleted: false,
  hasSeenLoading: false,
  hasShownToast: false,
  toastId: null,
};

/**
 * Handles destination account balance refresh.
 * Same permission + toast logic as SourceDestinationAccountDetail.
 *
 * @param {Object} options
 * @param {boolean} options.enabled - pass `showsBalance` (internal flow only)
 * @param {Function} options.onBalanceFetched
 */
const useBalanceRefresh = ({ enabled, onBalanceFetched }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [ensureAll] = useServiceChecker();

  const services = useSelector((state) => state.refreshToken.services);

  const hasBalanceService =
    ensureAll(["VAccountBalance"]).status || ensureAll(["Admin"]).status;

  const { isLoading, data, error } = useSelector(
    (state) => ({
      isLoading: state.ownerAllVerification.createTransactionBankRefreshLoading,
      data: state.ownerAllVerification.createTransactionBankRefreshData,
      error: state.ownerAllVerification.createTransactionBankRefreshError,
    }),
    shallowEqual,
  );

  const sessionRef = useRef({ ...IDLE_REFRESH_SESSION });

  const [isFetching, setIsFetching] = useState(false);

  const [trend, setTrend] = useState({
    ownerId: null,
    previousBalance: null,
  });

  useEffect(() => {
    const session = sessionRef.current;

    if (!session.isActive) {
      return;
    }

    if (isLoading) {
      session.hasSeenLoading = true;

      if (!session.hasShownToast) {
        session.hasShownToast = true;
        session.toastId = UniversalRefreshToast();
      }

      return;
    }

    if (session.isCompleted || !session.hasSeenLoading) {
      return;
    }

    const finish = (toastOptions) => {
      session.isCompleted = true;

      toast.dismiss(session.toastId);

      CommonToast({
        buttonNeeded: false,
        ...toastOptions,
      });

      sessionRef.current = { ...IDLE_REFRESH_SESSION };
    };

    if (data?.status) {
      finish({
        text: "Balance refreshed successfully.",
        icon: ICONS?.SELECT_ALL_WITH_WHITE_FILLED_CIRCLE,
        variant: "var(--success)",
      });
    } else if (error) {
      finish({
        text: error,
        icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
        variant: "var(--error)",
      });
    }
  }, [isLoading, data, error]);

  const refresh = useCallback(
    async ({ ownerId, previousBalance }) => {
      if (!enabled) {
        return;
      }
      if (
        !(services?.includes("VAccountBalance") || services?.includes("Admin"))
      ) {
        navigate("/feature_not_assigned", {
          state: {
            unavailableService: ["VAccountBalance", "Admin"],
          },
        });
        return;
      }

      if (sessionRef.current.isActive) {
        return;
      }

      sessionRef.current = {
        ...IDLE_REFRESH_SESSION,
        isActive: true,
      };

      setTrend({
        ownerId,
        previousBalance,
      });

      setIsFetching(true);

      try {
        const response = await dispatch(getCreateTransactionBalance(ownerId));

        if (response?.payload?.status === true) {
          onBalanceFetched?.(response.payload?.owner_accounts?.[0]);
        }
      } finally {
        setIsFetching(false);

        if (!sessionRef.current.hasSeenLoading) {
          sessionRef.current = { ...IDLE_REFRESH_SESSION };
        }
      }
    },
    [enabled, services, dispatch, navigate, onBalanceFetched],
  );

  return {
    refresh,
    isFetching,
    isBankRefreshLoading: isLoading,
    trend,
    hasBalanceService,
  };
};

/* =============================================================================
 * 3. SMALL BUILDING BLOCKS
 * ========================================================================== */

const TILE_CLASS =
  "flex flex-col w-width-full h-auto py-padding-sm px-padding-md bg-primary-nav rounded-border-radius-md gap-gap-xs";

const InfoTile = ({ label, children }) => (
  <div className={TILE_CLASS}>
    {children}

    <span className="text-xs text-text-secondary">{label}</span>
  </div>
);

const isRedactedBalance = (value) =>
  value === "xxxx.xx" || value === null || value === undefined || isNaN(+value);

/* =============================================================================
 * 4. BALANCE TILE
 * ========================================================================== */

const BalanceTile = ({
  balance,
  lastUpdated,
  isOwnerActive,
  ownerId,
  trend,
  isFetching,
  isBankRefreshLoading,
  canRefresh,
  onRefresh,
  refreshButtonTitle,
}) => {
  const hasBalance = Boolean(balance);

  const amount = hasBalance
    ? formatTotalBalance(Number(balance), "Default")
    : null;

  const meta = hasBalance ? formatTotalBalance(balance, "Default") : null;

  const balanceColor = !hasBalance
    ? "text-text-primary"
    : Number(balance) < GlobalConfiguration.lengthCheckZero
      ? "text-failed"
      : "text-settled";

  const balanceText = hasBalance
    ? `${isRedactedBalance(balance) ? "" : "INR "}${amount.amount} ${amount.abbr}`
    : globalText?.NOT_AVAILABLE;

  const captionText = isOwnerActive
    ? `${
        meta?.prefix === "crores"
          ? `Balance in ${meta.prefix} - `
          : globalText?.BALANCE_UPDATED
      } ${lastUpdated}`
    : "Balance";

  const showTrend = !isBankRefreshLoading && ownerId === trend.ownerId;

  const isIncrease =
    showTrend && Number(balance) > Number(trend.previousBalance);

  const isDecrease =
    showTrend && Number(balance) < Number(trend.previousBalance);

  return (
    <div className="flex flex-col w-width-full h-auto py-padding-sm px-padding-md bg-primary-nav rounded-border-radius-md gap-gap-base justify-between Qlg:flex-row Qlg:gap-gap-xs">
      <div className="w-width-full relative flex justify-between">
        <div className="flex flex-col gap-gap-xs">
          <div className="flex items-center gap-gap-sm relative">
            <div
              className={`flex text-left text-sm font-semibold ${balanceColor}`}
            >
              {balanceText}
            </div>

            {isFetching ? (
              <Loader
                height="var(--icon-sm)"
                width="var(--icon-sm)"
              />
            ) : (
              <div className="relative h-icon-sm w-icon-sm">
                {isIncrease && <SVGComponent url={ICONS.ARROW_UP_GREEN_GIF} />}

                {isDecrease && <SVGComponent url={ICONS.ARROW_DOWN_RED_GIF} />}
              </div>
            )}
          </div>

          <div className="text-left text-xs text-text-secondary whitespace-nowrap">
            <div className="w-full max-w-full overflow-hidden">
              <TickerAnimation text={captionText} />
            </div>
          </div>
        </div>

        {isOwnerActive && canRefresh && (
          <div className="flex flex-col justify-center">
            <CardActionableButton
              btnType="button"
              bgColor="var(--primary-nav-act)"
              disabledBgColor="var(--primary-nav-act)"
              handleFunction={onRefresh}
              icon={ICONS?.RELOAD_REFRESH_WHITE}
              hoverOption="white"
              title={refreshButtonTitle}
            />
          </div>
        )}
      </div>
    </div>
  );
};

/* =============================================================================
 * 5. MAIN COMPONENT
 * ========================================================================== */

const CommonDestinationAccount = ({
  transactionPayload,
  setTransactionPayload,
  header = globalText?.DESTINATION_ACCOUNT_CAPITALIZE,
  subHeader = globalText?.DESTINATION_ACCOUNT_DES_TEXT,
  onSelectAccount,
  onEditAccount,
  onViewAssociatedAccounts,
  showSelectAccount = true,
  isSelectDisabled = false,
  selectAccountButtonText = globalText?.SELECT_DESTINATION_ACCOUNT,
  showEditButton = true,
  isEditDisabled = false,
  editButtonTitle = globalText?.EDIT,
  showCreateAccount = true,
  isCreateDisabled = false,
  createAccountButtonText = globalText?.REGISTER_NEW_ACCOUNT,
  registerRoute = null,
  canCreateAccount = true,
  requiresSourceAccount = true,
  showsBalance = false,
  refreshButtonTitle = "Refresh",
  supportsAssociatedAccounts = true,
  showAssociatedAccounts = false,
  isAssociatedAccountsDisabled = false,
  associatedAccountsTitle = "Associated Accounts Found",
  associatedAccountsText,
  isReadOnly = false,
  isDisabled = false,
  payloadKeys,
  bankLogoVariant = "big",
}) => {
  const navigate = useNavigate();

  const keys = useMemo(
    () => ({
      ...DEFAULT_PAYLOAD_KEYS,
      ...payloadKeys,
    }),
    [payloadKeys],
  );

  const hasSourceAccount = Boolean(transactionPayload?.ownerBankAccountNo);

  const accountNumber = transactionPayload?.[keys.accountNumber];

  const ifsc = transactionPayload?.[keys.ifsc];

  const accountName =
    transactionPayload?.[keys.accountName] ||
    transactionPayload?.[keys.fallbackAccountName];

  const hasAccount = Boolean(accountNumber && ifsc);

  const shouldDisableDestination =
    isDisabled || (requiresSourceAccount && !hasSourceAccount);

  const selectDisabled =
    isReadOnly || shouldDisableDestination || isSelectDisabled;

  const editDisabled = isReadOnly || shouldDisableDestination || isEditDisabled;

  const createDisabled =
    isReadOnly ||
    shouldDisableDestination ||
    isCreateDisabled ||
    !canCreateAccount;

  const associatedAccountsDisabled = isReadOnly || isAssociatedAccountsDisabled;

  const handleBalanceFetched = useCallback(
    (ownerAccount) => {
      setTransactionPayload?.((prev) => ({
        ...prev,

        [keys.balanceLastUpdated]:
          ownerAccount?.owner_account_balance_last_fetched_at_in_words,

        [keys.balance]: ownerAccount?.owner_account_balance,
      }));
    },
    [setTransactionPayload, keys],
  );

  const {
    refresh,
    isFetching,
    isBankRefreshLoading,
    trend,
    hasBalanceService,
  } = useBalanceRefresh({
    enabled: showsBalance,
    onBalanceFetched: handleBalanceFetched,
  });

  const handleRefreshClick = useCallback(
    (event) => {
      event.stopPropagation();
      refresh({
        ownerId: transactionPayload?.[keys.ownerId],
        previousBalance: transactionPayload?.[keys.balance],
      });
    },
    [refresh, transactionPayload, keys],
  );

  const handleSelect = useCallback(() => {
    if (selectDisabled) {
      return;
    }

    onSelectAccount?.();
  }, [selectDisabled, onSelectAccount]);

  const handleEdit = useCallback(() => {
    if (editDisabled) {
      return;
    }

    (onEditAccount ?? onSelectAccount)?.();
  }, [editDisabled, onEditAccount, onSelectAccount]);

  const handleRegister = useCallback(() => {
    if (createDisabled || !registerRoute) {
      return;
    }

    navigate(registerRoute);
  }, [createDisabled, registerRoute, navigate]);

  const canShowAssociated =
    showAssociatedAccounts &&
    supportsAssociatedAccounts &&
    Boolean(onViewAssociatedAccounts);

  const handleAssociatedAccounts = useCallback(() => {
    if (associatedAccountsDisabled) {
      return;
    }

    onViewAssociatedAccounts?.();
  }, [associatedAccountsDisabled, onViewAssociatedAccounts]);

  return (
    <div className="w-width-full">
      <div
        className={`flex flex-col relative rounded-border-radius-md ${
          canShowAssociated ? "gap-gap-md" : ""
        }`}
      >
        <div
          className={`bg-primary-nav flex flex-col relative rounded-border-radius-md ${
            shouldDisableDestination
              ? "opacity-light-opacity cursor-not-allowed pointer-events-none"
              : ""
          }`}
        >
          <div className="p-padding-md h-height-full bg-primary-card flex flex-col rounded-border-radius-md">
            {showEditButton && !isReadOnly && hasAccount && (
              <div className="top-top-base right-right-base absolute">
                <IconTearDropButton
                  handleFunction={handleEdit}
                  isDisabled={editDisabled}
                  isLoading={false}
                  enabledBgColor="var(--primary-nav-act)"
                  icon={ICONS?.EDIT}
                  hoverOption="white"
                  title={editButtonTitle}
                />
              </div>
            )}

            <div
              className={`flex flex-col gap-gap-md ${
                hasAccount ? "mb-margin-xl" : ""
              }`}
            >
              <div className="flex flex-col gap-gap-xs">
                <span className="text-font-md text-text-primary font-semibold">
                  {header}
                </span>

                <span className="text-xs text-text-secondary">{subHeader}</span>
              </div>

              {!hasAccount ? (
                <div>
                  {showSelectAccount && (
                    <PrimaryWithIconButton
                      btnText={selectAccountButtonText}
                      enabledBgColor="var(--primary-nav-act)"
                      enabledTextColor="var(--text-primary)"
                      isDisabled={selectDisabled}
                      handleFunction={handleSelect}
                      iconToBeAlign="left"
                      icon={ICONS?.SELECT_ACCOUNT}
                      fullWidthRequired
                      hoverOption="white"
                    />
                  )}

                  {showCreateAccount && registerRoute && (
                    <div className="mt-margin-md">
                      <PrimaryWithIconButton
                        btnText={createAccountButtonText}
                        enabledBgColor="var(--primary-nav-act)"
                        enabledTextColor="var(--text-primary)"
                        imgColor="var(--text-primary)"
                        isDisabled={createDisabled}
                        handleFunction={handleRegister}
                        iconToBeAlign="left"
                        icon={ICONS?.ADD_BUTTON_WHITE}
                        fullWidthRequired
                        hoverOption="white"
                      />
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <div className="flex flex-col gap-gap-md">
                    <InfoTile label={globalText?.ACCOUNT_NUMBER}>
                      <span className="text-sm text-text-primary font-semibold">
                        {formatAccountNumber(accountNumber)}
                      </span>
                    </InfoTile>

                    <InfoTile label={globalText?.ACCOUNT_NAME}>
                      <div className="text-sm text-text-primary font-semibold">
                        <div className="flex-1 truncate flex">
                          <TickerAnimation text={accountName} />
                        </div>
                      </div>
                    </InfoTile>

                    {showsBalance && (
                      <BalanceTile
                        balance={transactionPayload?.[keys.balance]}
                        lastUpdated={
                          transactionPayload?.[keys.balanceLastUpdated]
                        }
                        isOwnerActive={transactionPayload?.[keys.isOwnerActive]}
                        ownerId={transactionPayload?.[keys.ownerId]}
                        trend={trend}
                        isFetching={isFetching}
                        isBankRefreshLoading={isBankRefreshLoading}
                        canRefresh={hasBalanceService}
                        onRefresh={handleRefreshClick}
                        refreshButtonTitle={refreshButtonTitle}
                      />
                    )}
                  </div>

                  <div className="flex items-center justify-between absolute right-right-base bottom-bottom-base">
                    <div className="bg-text-primary rounded-tl-border-radius-md rounded-br-border-radius-md flex items-center justify-around w-width-full h-full py-padding-sm px-padding-md">
                      <CommonBankLogo
                        ifsc={ifsc}
                        variant={bankLogoVariant}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {canShowAssociated && (
          <div
            role="button"
            tabIndex={associatedAccountsDisabled ? -1 : 0}
            onClick={
              associatedAccountsDisabled ? undefined : handleAssociatedAccounts
            }
            onKeyDown={(e) => {
              if (
                !associatedAccountsDisabled &&
                (e.key === "Enter" || e.key === " ")
              ) {
                e.preventDefault();
                handleAssociatedAccounts();
              }
            }}
            className={`w-width-full flex items-start justify-between gap-gap-md bg-primary-card rounded-border-radius-md p-padding-md relative z-z-index-base ${
              associatedAccountsDisabled
                ? "opacity-light-opacity pointer-events-none"
                : "cursor-pointer global-hover-white"
            }`}
          >
            <div className="flex flex-col gap-gap-sm flex-1 min-w-0">
              <p className="text-text-primary font-semibold text-font-md">
                {associatedAccountsTitle}
              </p>

              <ul className="list-disc pl-padding-md text-text-secondary text-font-xs flex flex-col gap-gap-sm">
                {[]
                  .concat(associatedAccountsText)
                  .filter(Boolean)
                  .map((point, i) => (
                    <li key={i}>{point}</li>
                  ))}
              </ul>
            </div>
            <div className="flex-shrink-0 pt-padding-xs">
              <SVGComponent
                url={ICONS?.ARROW_RIGHT_WHITE}
                color="var(--text-primary)"
                height="var(--icon-sm)"
                width="var(--icon-sm)"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommonDestinationAccount;
